import { useCallback, useMemo } from 'react'
import { pdfCmOffsetToWorldMeters, worldMetersToPdfCmOffset } from '@constants'
import { useControlStore } from '@editorStores'
import StoreyImageOriginControl from 'src/components/scene/StoreyImageOriginControl'
import useStoreyImageOriginStore, {
  STOREY_IMAGE_ORIGIN_SELECTION_KEY,
  STOREY_IMAGE_REFERENCE_SELECTION_MESSAGE,
} from 'src/components/scene/StoreyImageOriginControl/store'
import { ScenePosition } from 'src/components/scene/hooks/useScenePositionDrag'
import useSceneElement from './useSceneElement'
import useSelectionMode from './useSelectionMode'

interface Params {
  storey: string
  transform: StoreyImageTransform
  onUpdateTransform: (storey: string, transform: StoreyImageTransform) => void
}

/** Normalize an angle in degrees to the [-180, 180] range of the rotation text field. */
const normalizeRotationDegrees = (degrees: number): number =>
  ((((degrees + 180) % 360) + 360) % 360) - 180

/**
 * "In Szene" workflow for a storey's floorplan image: `start` enters a selection mode in which
 * the user first picks a reference point (the fixed target) and then the new origin point on the
 * plan; the injected `StoreyImageOriginControl` scene element shows a movable/rotatable x/y axes
 * gizmo at the second point, and applying it translates (and rotates) the image so the gizmo
 * origin ends up on the reference point.
 */
const useStoreyImageOriginTool = ({ storey, transform, onUpdateTransform }: Params) => {
  const { setSelectionMode } = useSelectionMode()
  const selectionModeKey = useControlStore(state => state.selectionModeKey)

  const activeStorey = useStoreyImageOriginStore(state => state.activeStorey)
  const origin = useStoreyImageOriginStore(state => state.origin)
  const startTool = useStoreyImageOriginStore(state => state.startTool)
  const reset = useStoreyImageOriginStore(state => state.reset)

  const isSelecting = selectionModeKey === STOREY_IMAGE_ORIGIN_SELECTION_KEY
  const isActive = activeStorey === storey && (isSelecting || origin !== null)

  const start = useCallback(() => {
    startTool(storey)
    setSelectionMode({
      key: STOREY_IMAGE_ORIGIN_SELECTION_KEY,
      message: STOREY_IMAGE_REFERENCE_SELECTION_MESSAGE,
    })
  }, [setSelectionMode, startTool, storey])

  const onApply = useCallback(
    (referencePoint: ScenePosition, gizmoOrigin: ScenePosition, gizmoRotation: number) => {
      // Applying moves the gizmo origin (picked on the plan) onto the reference point and rotates
      // the image around the reference point by −gizmo angle so the gizmo's axes align with the
      // global axes. Composed onto the image transform (offset translation, then rotation around
      // the offset point) the new world offset becomes `reference + R(−angle)·(offset − gizmo)`,
      // and the gizmo angle is subtracted from the image rotation.
      const scale = transform.scale || 100
      const offsetToGizmoX = pdfCmOffsetToWorldMeters(transform.offsetX, scale) - gizmoOrigin.x
      const offsetToGizmoY = pdfCmOffsetToWorldMeters(transform.offsetY, scale) - gizmoOrigin.y
      const cos = Math.cos(-gizmoRotation)
      const sin = Math.sin(-gizmoRotation)
      onUpdateTransform(storey, {
        ...transform,
        offsetX: worldMetersToPdfCmOffset(
          referencePoint.x + offsetToGizmoX * cos - offsetToGizmoY * sin,
          scale,
        ),
        offsetY: worldMetersToPdfCmOffset(
          referencePoint.y + offsetToGizmoX * sin + offsetToGizmoY * cos,
          scale,
        ),
        rotation: normalizeRotationDegrees(transform.rotation - (gizmoRotation * 180) / Math.PI),
        offsetsInPdfCm: true,
      })
      reset()
    },
    [onUpdateTransform, reset, storey, transform],
  )

  const properties = useMemo(() => ({ storey, onApply }), [storey, onApply])

  useSceneElement({
    enabled: isActive,
    properties,
    component: StoreyImageOriginControl,
  })

  return { start, isActive }
}

export default useStoreyImageOriginTool
